<template>
  <div class="site-footer critical-component-clear container">
    <div class="site-footer__row">
      <div class="col xs4 l4 site-footer__selector-gap">
        <a
          :href="$variable('routes.root')"
          class="site-footer__logo"
          aria-labelledBy="site-footer-title"
        >
          <h2 id="site-footer-title">
            <span
              class="visually-hidden"
              v-text="$variable('store.shopName')"
            />
            <brand-logo />
          </h2>
        </a>
      </div>

      <div
        v-if="!isMobile"
        class="col xs4 l6-11 site-footer__selector-gap site-footer__line-gap"
        aria-labelledby="multi-store-title"
      >
        <multi-store-drawer-trigger
          id="multi-store-title"
          namespace="multiStoreDrawer"
        />
      </div>
    </div>

    <div class="site-footer__row site-footer__row--medium">
      <div
        class="col xs4 l3 site-footer__newsletter"
        aria-labelledby="newsletter-title"
      >
        <newsletter-signup
          id="newsletter-title"
          :newsletter-data="newsletter"
          namespace="site-footer"
        />
      </div>

      <accordion-snippet
        v-if="isMobile"
        class="site-footer__links-column site-footer__links-column--mobile col xs4"
        :accordion-data="getFooterAccordionData"
        aria-labelledby="footer-links-mobile-title"
      />

      <multi-store-drawer-trigger
        v-if="isMobile"
        class="col xs4"
        namespace="multiStoreDrawer"
      />
      <div class="site-footer__columns">
        <div
          v-for="(column, index) of footerLinks"
          :key="index"
          class="site-footer__links-column site-footer__links-column--desktop"
          :aria-labelledby="`footer-links-title-${index}`"
          :class="[
            getLinkColumnClass(index),
            {
              'margin-left-0': index === 0,
              'margin-left-1': index === 1,
              'margin-left-2': index === 2,
            }
          ]"
        >
          <div
            v-if="!isMobile"
            :id="`footer-links-title-${index}`"
          >
            <h3
              class="text-other-label-regular"
              v-text="column.title"
            />

            <ul class="site-footer__links-list">
              <li
                v-for="(link, linkIndex) of column.linkList"
                :key="linkIndex"
                :aria-labelledby="`footer-links-subtitle-${link?.title}`"
              >
                <a
                  :id="`footer-links-subtitle-${link?.title}`"
                  :href="link.url"
                  :aria-current="link.active"
                  class="text-body-m-regular site-footer__link"
                  v-text="link.title"
                />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div
      class="site-footer__row "
      :class="[
        footerExchange ? 'site-footer__exchange' : 'site-footer__row--large']"
    >
      <div class="col xs1-2 l4 site-footer__copyright">
        <p
          class="text-body-m-regular site-footer__domain"
          v-text="$variable('store.localisedShopDomain').replaceAll('/', '')"
        />

        <p
          class="text-body-m-regular"
          v-text="$string('general.site_footer.copyright', {replace: {
            current_year: new Date().getFullYear()
          }})"
        />
      </div>

      <ul
        v-if="!isMobile"
        class="col l5-8 site-footer__policy-links"
        aria-labelledby="footer-policy-links-title"
      >
        <li
          v-for="(policyLink, index) of policyLinks"
          id="footer-policy-links-title"
          :key="index"
        >
          <a
            :href="policyLink.url"
            :aria-current="policyLink.active"
            class="text-body-m-regular site-footer__link"
            v-text="policyLink.title"
          />
        </li>
      </ul>
      <div class="col xs3-4 l10-12 site-footer__social-gap">
        <ul
          class="site-footer__social"
          aria-labelledby="footer-social-title"
        >
          <li
            v-for="icon of socialIcons"
            id="footer-social-title"
            :key="icon.type"
          >
            <a
              :href="icon.link"
              target="_blank"
              rel="noopener noreferrer"
              :aria-label="$string(
                'general.site_footer.follow_us',
                { replace: { platform: icon.title } }
              )"
            >
              <span class="visually-hidden">
                {{ icon.title }}
              </span>
              <responsive-image
                :image="icon.image"
                class="site-footer__social-icon"
                ratio="1:1"
                widths="24, 2x, 3x"
              />
            </a>
          </li>
        </ul>
      </div>
    </div>

    <teleport to="body">
      <overlay
        id="multi-store-drawer"
        key="multiStoreDrawer"
        block-class="multi-store-drawer"
        direction="up-right"
        namespace="multiStoreDrawer"
        type="drawer"
      >
        <template #title>
          <span v-text="$string('general.multi_store.drawer.title')" />
        </template>

        <template #body="overlay">
          <multi-store-drawer :overlay="overlay" />
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>

/**
 * Vue: Site footer (site-footer)
 * -----------------------------------------------------------------------------
 * Site footer component with accordion display of links.
 *
 * @param {Array} footerLinks - Array of linklists and corresponding titles.
 * @param {Object} newsletter - Data for newsletter signup component.
 * @param {Array} policyLinks - Linklist of policy links.
 * @param {Object} socialLinks - Social media links.
 */
import { mapState } from 'vuex'

import MultiStoreDrawerTrigger from '~async/multi-store-drawer-trigger/multi-store-drawer-trigger'
import MultiStoreDrawer from '~async/multi-store-drawer/multi-store-drawer'
import NewsletterSignup from '~async/newsletter-signup/newsletter-signup'
import AccordionSnippet from '~global/accordion-snippet/accordion-snippet'
import ResponsiveImage from '~global/images/responsive-image.vue'
import Overlay from '~global/overlay/overlay'

import BrandLogo from '~icons/general/brand-logo.svg'

export default {
  name: 'SiteFooter',

  components: {
    AccordionSnippet,
    BrandLogo,
    MultiStoreDrawer,
    MultiStoreDrawerTrigger,
    NewsletterSignup,
    ResponsiveImage,
    Overlay,
  },

  props: {
    socialIcons: {
      type: Array,
      required: true,
    },
    footerLinks: {
      type: Array,
      default: () => [],
    },
    newsletter: {
      type: Object,
      default: () => ({
        copy: '',
        customSource: '',
        title: '',
        titleElement: '',
      }),
    },
    policyLinks: {
      type: Array,
      default: () => [],
    },
    footerExchange: {
      type: Boolean,
      default: false,
    },
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Return formatted data for accordion snippet component, which requires
     * an array of objects with `title` and `body` properties.
     * @returns {Array}
     */
    getFooterAccordionData() {
      const accordionData = this.footerLinks?.filter((column) => column?.title).map((column) => {
        const formattedBody = `
          <ul class="site-footer__accordion-content">
            ${this.getFooterColumn(column?.linkList)}
          </ul>
        `

        return {
          title: column?.title,
          body: formattedBody,
        }
      })

      return accordionData
    },
  },

  methods: {

    getIconComponent(type) {
      switch (type) {
        case 'facebook':
          return 'IconFacebook'
        case 'instagram':
          return 'IconInstagram'
        case 'pinterest':
          return 'IconPinterest'
        case 'tiktok':
          return 'IconTiktok'
        default:
          return null
      }
    },

    /**
     * Return a formatted series of `<li>` elements containing anchor links.
     * @param {Array} linklist - Footer linklist.
     * @returns {String}
     */
    getFooterColumn(linklist) {
      let column = ''

      linklist.forEach(link => {
        const listitem = `
          <li>
            <a
              href="${link.url}"
              aria-current="${link.active}"
              class="text-body-m-regular site-footer__link"
            >
              ${link.title}
            </a>
          </li>
        `

        column = column.concat(' ', listitem)
      })

      return column
    },

    /**
     * Posiiton footer links based on index.
     * @param {Number} index - Index of footer column links.
     * @returns {String}
     */
    getLinkColumnClass(index) {
      let columnClass = 'l6-7'

      if (index === 1) {
        columnClass = 'l8-9'
      } else if (index === 2) {
        columnClass = 'l10-11'
      }

      return columnClass
    },
  },
}
</script>

<style lang="scss">
@import '~async/newsletter-signup/newsletter-signup';
@import './site-footer';
</style>
